import { CSVLink } from 'react-csv';
import { Navigate, useNavigate } from 'react-router-dom';
import { DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import { Alert, Button, Collapse, Form, Result, Select, Space, Table, Upload } from 'antd';
import { AxiosError } from 'axios';

import { Rules, Tables } from '@app/web-components';

import { useBundleViewPanel, useFindBundleById } from '../../../bundles';
import { useBulkCreateSubscription } from '../../../subscriptions/data';
import {
  JustificationTooltipText,
  SelectAccessTooltipText,
  useBundleSelect,
  useJustificationFormItem,
  useSelectAccessesFormItem,
} from '../../../subscriptions/forms/inputs';
import { useBulkSubscriptionSelectAccess } from '../atoms';

import { SelectUserAccessTooltipText, useFileUpload, useSelectUserAccessFormItem } from './inputs';
import { useBulkSubscribeAccess } from './rules';

type FormData = {
  bundleId: number;
  bundleAccessIds: number[];
  userAccesses: string[];
  justification?: string;
};

export type CreateFormProps = {
  bundleId: number | undefined;
  onChange: (e: { bundleId?: number }) => void;
};

const BundleTooltipText = () => (
  <>
    The <strong>Bundle</strong> is the collection of accesses that can be granted to the worker. When you select a
    bundle, the details will appear below the bundle field.
    <ul>
      <li>A bundle must be selected.</li>
      <li>The bundle's employee type must match each of the worker's employee type in the file.</li>
    </ul>
  </>
);

export const CreateForm = ({ bundleId, onChange }: CreateFormProps) => {
  const navigate = useNavigate();
  const { getTableHeight } = Tables.useReactiveTableHeight();
  const { canBulkSubscribeAccess } = useBulkSubscribeAccess();
  const [form] = Form.useForm();
  const bulkCreateSubscription = useBulkCreateSubscription();
  const selectedBundleId = Form.useWatch('bundleId', form);
  const selectedAccesses = Form.useWatch('bundleAccessIds', form);

  const bundleQuery = useFindBundleById({
    bundleId: selectedBundleId || bundleId || 0,
    enabled: !!selectedBundleId || !!bundleId,
  });

  const { getProps: getBundleSelectProps } = useBundleSelect({ isOwnedBundles: true });
  const { getProps: getJustificationFormItemProps } = useJustificationFormItem(bundleQuery.data);
  const { getSelectAccessTableProps: props, selectAccessTourStepProps } = useBulkSubscriptionSelectAccess(
    selectedBundleId || bundleId,
    form,
  );
  const { getProps: getSelectAccessesFormItemProps } = useSelectAccessesFormItem({
    props,
    form,
    selectAccessTourStepProps,
  });
  const { getPanelSpaceProps } = useBundleViewPanel(selectedBundleId || bundleId);
  const { uploadProps: getUploadFileProps, bulkUploadTableData } = useFileUpload(
    selectedBundleId || bundleId,
    selectedAccesses,
  );
  const { getProps: getSelectUserAccessFormItemProps } = useSelectUserAccessFormItem(
    bulkUploadTableData,
    form,
    bundleQuery?.data?.name ?? '',
  );

  if (bundleQuery.error) {
    const error = bundleQuery.error as AxiosError;

    if (error.response?.status === 404) {
      return <Result status="404" title="This bundle does not exist or was deleted." />;
    }

    return <Result status="error" title="Something went wrong getting the bundle." />;
  }

  if (!bundleQuery.isPending && bundleQuery.data && !bundleQuery.data.isActive) {
    return (
      <Result
        status="error"
        title="Invalid bundle subscription"
        subTitle={`Cannot subscribe to inactive bundle ${bundleQuery?.data?.name}`}
        extra={[
          <Button type="primary" key="go to subscriptions" onClick={() => navigate('../')}>
            Go To Subscriptions
          </Button>,
        ]}
      />
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleChangedValues = (changedValues: any, values: any) => {
    if (changedValues.bundleId) {
      // keep our parent up to date so it can update the url search params as the form changes
      onChange({
        bundleId: changedValues.bundleId || bundleId,
      });
      // Reset bundleAccessIds if either bundleId has changed
      form.setFieldsValue({
        bundleAccessIds: [],
        userAccesses: [],
      });
    }
  };

  const handleResetForm = () => {
    form.resetFields();
    bulkCreateSubscription.reset();
  };

  const handleSubmit = (data: FormData) => {
    bulkCreateSubscription.mutate({
      bundleId: data.bundleId,
      bundleAccessIds: data.bundleAccessIds,
      userAccesses: data.userAccesses,
      justification: data.justification,
    });
  };

  if (bulkCreateSubscription.isSuccess && bulkCreateSubscription.data.data.isSuccess) {
    return (
      <Result
        status="success"
        title={`Successfully bulk subscribed to ${bundleQuery.data?.name}!`}
        extra={[
          <Button type="primary" key="go to subscriptions" onClick={() => navigate('../')}>
            Go To Subscriptions
          </Button>,
          <Button
            key="subscribe to another"
            onClick={() => {
              bulkCreateSubscription.reset();
              form.resetFields();
              onChange({ bundleId });
            }}
          >
            Bulk Subscribe To Another
          </Button>,
          <CSVLink
            data={
              bulkCreateSubscription.data.data.mutated?.map((x) => ({
                enterpriseId: x.user.enterpriseId,
                name: x.user.name,
              })) ?? []
            }
            filename={`bulk_subscription_${bulkCreateSubscription.data.data.mutated?.[0]?.bundle.name}.csv`}
          >
            <Button icon={<DownloadOutlined />}>Export Subscribed to CSV</Button>
          </CSVLink>,
          <Space style={{ marginTop: 20 }}>
            <Table
              size="small"
              columns={[
                {
                  title: 'Enterprise Id',
                  dataIndex: ['user', 'enterpriseId'],
                },
                {
                  title: 'User Name',
                  dataIndex: ['user', 'name'],
                },
                {
                  title: 'Status',
                  dataIndex: 'status',
                  render: (_, record) => (
                    <Button
                      type="link"
                      size="small"
                      style={{ padding: 3 }}
                      onClick={() => window.open(`../subscriptions/${record.id}`, '_blank')}
                    >
                      Successfully subscribed!
                    </Button>
                  ),
                },
              ]}
              dataSource={bulkCreateSubscription.data.data.mutated ?? []}
              scroll={{ y: getTableHeight() }}
            />
          </Space>,
        ]}
      />
    );
  }

  const instructions = [
    {
      key: '1',
      label: 'Instructions',
      children: (
        <>
          <p>
            Bulk Subscriptions are used to grant the accesses within the selected bundle for each of the worker in the
            file.
          </p>
          <p>
            <BundleTooltipText />
          </p>
          <p>
            When you select a bundle, you will be given the option to <strong>Select Accesses</strong> under the bundle
            to subscribe to. You have to select at least one access before uploading the file. The workers identified by
            the WWIDs in the file should not already have subscribed to the bundle access selected.
            <SelectAccessTooltipText
              paragraphs={
                <p>
                  Subscribers should request access to only the bundle items needed to perform their work. User can
                  select item per item or all the items by clicking in the top selection box.
                </p>
              }
            />
          </p>
          <p>
            <JustificationTooltipText />
          </p>
          <p>
            The file should be a text file (.txt) and should contain (WWIDs) in the format shown below:
            <ul>
              <div>WWID1</div>
              <div>WWID2</div>
              <div>WWID3</div>
              <div>WWID4</div>
            </ul>
          </p>
          <p>
            Please note that SAAF is not replacing AGS or IEM, these are just underlying applications to create the
            bundles, hence none of their regular functionalities, conditions, approval workflows or anything else is
            modified, removed or changed by SAAF. This means SAAF will trigger the bundle item request to those tools.
          </p>
          <p>
            <SelectUserAccessTooltipText />
          </p>
        </>
      ),
    },
  ];

  if (bundleQuery.data?.owners && !canBulkSubscribeAccess(bundleQuery.data?.owners)) {
    return <Navigate to="/access-forbidden" />;
  }

  return (
    <Form
      form={form}
      layout="vertical"
      style={{ width: '100%', minWidth: 1000 }}
      disabled={bulkCreateSubscription.isPending || bulkCreateSubscription.isSuccess}
      onFinish={handleSubmit}
      initialValues={{
        bundleId,
      }}
      requiredMark
      onValuesChange={handleChangedValues}
    >
      {bulkCreateSubscription.error ? (
        <Alert
          message="Error"
          description={`Something went wrong. Error: ${bulkCreateSubscription.error}`}
          type="error"
          showIcon
          style={{ marginBottom: 20 }}
        />
      ) : undefined}
      {bulkCreateSubscription.isSuccess && !bulkCreateSubscription.data.data.isSuccess ? (
        <Alert
          message="Sorry, subscribing to this bundle is not possible."
          description={<Rules.RuleMessagesPanel messages={bulkCreateSubscription.data.data.messages} />}
          type="error"
          showIcon
          style={{ marginBottom: 20 }}
        ></Alert>
      ) : undefined}
      <Collapse items={instructions} style={{ marginBottom: '1em' }} />
      <Form.Item label="Bundle" name="bundleId" tooltip={<BundleTooltipText />}>
        <Select {...getBundleSelectProps()} />
      </Form.Item>
      {bundleId ? <Space {...getPanelSpaceProps({ style: { marginBottom: '1.5em' } })} /> : undefined}
      <Form.Item {...getSelectAccessesFormItemProps()} />
      <Form.Item {...getJustificationFormItemProps()} />
      <Space direction="horizontal" style={{ marginBottom: 20 }}>
        <Upload {...getUploadFileProps}>
          <Button
            title="Please select atleast one access."
            disabled={!selectedAccesses || selectedAccesses?.length === 0}
            icon={<UploadOutlined />}
          >
            Click to Upload (Max: 1)
          </Button>
        </Upload>
      </Space>
      <Form.Item {...getSelectUserAccessFormItemProps()} />
      <Space style={{ marginTop: 20 }}>
        <Button type="primary" htmlType="submit" loading={bulkCreateSubscription.isPending}>
          Submit
        </Button>
        <Button onClick={handleResetForm}>Reset</Button>
        <Button onClick={() => navigate('../')}>Cancel</Button>
      </Space>
    </Form>
  );
};
